import { Select as SelectPrimitive } from '@base-ui/react'
import { Check, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CircleFlag } from '@/components/ui/CircleFlag'
import { SUPPORTED_LANGUAGES, type Language } from '@/i18n/config'
import { cn } from '@/lib/cn'

/* The interface-language switch. Both languages are first-class — Russian is
 * not a translation layer over an English product (EXPERIENCE.md § Foundation,
 * FR-58) — so this control never reads as "English, plus a translation": it
 * names each language in that language and marks the current one, rather than
 * offering one alternative to a presumed default.
 *
 * A select rather than the segmented toggle it replaces. A toggle shows every
 * option at full weight forever, which is right for a range filter the user
 * changes constantly and wrong for a preference set once and then only read.
 * Collapsed, the control answers "what language am I in?" in one glance and
 * gives the topbar its width back.
 *
 * FLAGS ARE CIRCULAR SVGs (CircleFlag), as on the country and phone pickers,
 * and they used to be emoji here. The cost of the emoji was concrete: Windows
 * ships no emoji-flag glyphs, so Chrome and Edge rendered 🇬🇧 as a boxed `GB`
 * — for users who are on Windows. The SVG renders everywhere and matches the
 * other two pickers, which is the point of changing it.
 *
 * The flag is still never the label: the trigger and every option carry text,
 * so the flag remains an `aria-hidden` decoration and never the only carrier of
 * meaning.
 *
 * A flag is also not a language, strictly: GB is a country and English is
 * spoken in rather more of them. It is used here as recognisable shorthand in
 * a two-option control where the text is always present, not as the identifier.
 */

/** Each language names ITSELF, untranslated. A language switch is read by
 *  someone who wants the OTHER language, and "Russian" is no help to a reader
 *  who does not read English.
 *
 *  `country` is the flag drawn beside it — a country code, per the note above,
 *  not a claim that the language belongs to it. */
const LANGUAGE_META: Record<Language, { country: string; native: string }> = {
  en: { country: 'GB', native: 'English' },
  ru: { country: 'RU', native: 'Русский' },
}

export interface LanguageSelectProps {
  value: Language
  onChange: (language: Language) => void
  /** `compact` shows the short code (EN) for persistent chrome; `full` shows
   *  the native name, for a settings surface with room to be explicit. */
  variant?: 'compact' | 'full'
  className?: string
}

export function LanguageSelect({
  value,
  onChange,
  variant = 'compact',
  className,
}: LanguageSelectProps) {
  const { t } = useTranslation()
  const active = LANGUAGE_META[value]

  return (
    /* The primitive emits the same `role="combobox"` trigger and
       `role="listbox"` / `role="option"` list this control assembled by hand,
       and takes over the four effects that made it work: Escape closing and
       returning focus to the trigger, pointerdown-outside dismissal, focusin
       dismissal, and focusing the current language on open. The wrapping
       `<div className="relative">` goes with them — the popup is positioned by
       the primitive now, not by an offset parent. */
    <SelectPrimitive.Root<Language>
      value={value}
      onValueChange={(next) => {
        // `null` is the primitive's "cleared"; there is no clear affordance
        // here and the interface language always has a value.
        if (next !== null) onChange(next)
      }}
      modal={false}
    >
      <SelectPrimitive.Trigger
        // The trigger's own text is a bare code or a language name; on its own
        // it never says WHAT is being chosen, so the label supplies the noun.
        aria-label={`${t('common.language.label')}: ${active.native}`}
        className={cn(
          'border-neutral-100 bg-surface flex w-full cursor-pointer items-center gap-1.5',
          'rounded-md border transition-colors',
          'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
          'hover:bg-surface-hover',
          variant === 'compact'
            ? 'h-8 px-2 text-[11.5px]'
            : 'h-9 justify-between px-2.5 text-[13px]',
          className,
        )}
      >
        <CircleFlag code={active.country} size={variant === 'compact' ? 15 : 17} />
        <span className="text-text-heading font-medium">
          {variant === 'compact' ? t(`common.language.${value}`) : active.native}
        </span>
        <ChevronDown
          size={13}
          aria-hidden
          className={cn(
            'text-text-muted shrink-0 transition-transform',
            'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
            variant === 'compact' ? 'ml-0.5' : '',
            'data-popup-open:rotate-180',
          )}
        />
      </SelectPrimitive.Trigger>

      {/* Portaled, where this popup used to be an absolutely-positioned child.
          `z-[60]` and not the old `z-40`: out in a portal the popup is a
          SIBLING of any open sheet rather than a descendant, so it no longer
          inherits its way above one — `z-40` would put the language menu under
          a `z-50` scrim. Same reasoning, same value as Combobox.tsx. */}
      <SelectPrimitive.Portal>
        <SelectPrimitive.Positioner
          sideOffset={4}
          align="end"
          /* Off: the default aligns the SELECTED ITEM with the trigger, which
             would move the whole menu when the language changes. This control
             has always opened below its button. */
          alignItemWithTrigger={false}
          className="z-[60]"
        >
          <SelectPrimitive.Popup
            aria-label={t('common.language.label')}
            className={cn(
              'bg-surface border-border-subtle flex flex-col gap-0.5 rounded-md border p-1',
              'shadow-[var(--shadow-overlay)]',
              'origin-(--transform-origin)',
              'transition-[opacity,transform] duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
              'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
              // `w-full` becomes the anchor's measured width: a portaled popup
              // has no offset parent to be 100% of any more.
              variant === 'compact' ? 'w-[168px]' : 'w-(--anchor-width)',
            )}
          >
            <SelectPrimitive.List>
              {SUPPORTED_LANGUAGES.map((language) => {
                const meta = LANGUAGE_META[language]
                return (
                  <SelectPrimitive.Item
                    key={language}
                    value={language}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1.5',
                      'text-left text-[12.5px] transition-colors',
                      'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
                      'data-highlighted:bg-surface-hover',
                      'data-selected:text-text-heading data-selected:font-medium',
                      'text-text-body font-normal',
                    )}
                  >
                    <CircleFlag code={meta.country} size={17} />
                    <SelectPrimitive.ItemText render={<span className="flex-1" />}>
                      {meta.native}
                    </SelectPrimitive.ItemText>
                    {/* The tick is redundant with aria-selected for a screen
                        reader, hence aria-hidden — it is here for the eye.
                        ItemIndicator mounts only while selected, which is what
                        replaces the `selected ? … : null` conditional. */}
                    <SelectPrimitive.ItemIndicator render={<span className="shrink-0" />}>
                      <Check size={14} aria-hidden className="text-brand" />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                )
              })}
            </SelectPrimitive.List>
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
